import React, { memo } from "react";
import Select, { Props as ReactSelectProps, FormatOptionLabelMeta, ValueType, components } from "react-select";
import customXGSSelectStyles from "./customXGSSelectStyles";
import XGSAsyncSelect from "./xgsAsyncSelect";
import "./xgsSelect.scss";
import Button, { ButtonSizes, ButtonThemes } from "../button/button";

export interface XGSSelectOption {
  value: string | boolean;
  label: string;
  subtitle?: string;
  valueForSearch?: string;
  isFixed?: boolean;
  extendAction?: () => void;
  extendLabel?: string;
  isDisabled?: boolean;
}

export interface GroupedOption {
  label: string;
  options: XGSSelectOption[];
}

export interface XGSSelectProps extends ReactSelectProps<XGSSelectOption> {
  onValueChange?: (option?: XGSSelectOption | null) => void;
  onMultiValuesChange?: (option?: XGSSelectOption[] | null) => void;
  async?: boolean;
  clearable?: boolean;
}

const XGSSelect: React.FC<XGSSelectProps> = memo(({ async, clearable, ...props }) => {
  const formatOptionLabel = (
    option: XGSSelectOption,
    labelMeta: FormatOptionLabelMeta<XGSSelectOption>
  ) => {
    const isMenu = labelMeta.context === "menu";
    return (
      <div
        style={{ display: "flex", flexDirection: isMenu ? "column" : "row" }}
      >
        <div>{option.label}</div>
        <div style={{ marginLeft: isMenu ? 0 : 10 }}>{option.subtitle}</div>
      </div>
    );
  };

  const formatGroupLabel = (group: GroupedOption) => (<div className="xgs-select__group-label">{group.label}</div>);

  const mergedComponents = {
    ...(props.components || {}),
    IndicatorSeparator: () => null,
    MenuList: (menuListProps: any) => (
      <components.MenuList {...menuListProps}>
        {menuListProps.children}
        {props.extendAction && (
          <div className="xgs-select__add-item-button" >
            <Button theme={ButtonThemes.transparent} size={ButtonSizes.auto} onClick={props.extendAction}>{props.extendLabel  || "Add new"}</Button>
          </div>
        )}
      </components.MenuList>
    ),
  };

  const commonOptions: XGSSelectProps = {
    ...props,
    formatOptionLabel,
    formatGroupLabel,
    styles: props.styles ? { ...props.styles, ...customXGSSelectStyles } : customXGSSelectStyles,
    classNamePrefix: "xgs-select",
    /** Do not let `isClearable: clearable` overwrite `isClearable` from ...props when `clearable` prop is omitted. */
    isClearable: clearable !== undefined ? clearable : props.isClearable,
    components: mergedComponents,
    onChange: (v: ValueType<any>) => {
      if (!v) {
        props.onValueChange && props.onValueChange(null);
        props.onMultiValuesChange && props.onMultiValuesChange(null);
      } else {
        if (v && v.length > 0) {
          // multi-select case
          const options = v;
          props.onMultiValuesChange && props.onMultiValuesChange(options);
        } else {
          // single-select choosed
          const options = {
            label: v?.label,
            subtitle: v?.subtitle,
            value: v?.value
          };
          props.onValueChange && props.onValueChange(options);
          // or multi-select resetted
          props.onMultiValuesChange && props.onMultiValuesChange(null);
        }
      }
    }
  };

  return (
    <>
      {!async && (
        <Select
          {...commonOptions}
          isDisabled={props.disabled}
        />
      )}
      {async && <XGSAsyncSelect
        {...commonOptions}
        isDisabled={props.disabled}
      />}
    </>
  );
});

export default XGSSelect;